import * as XLSX from 'xlsx-js-style';

import type { ExportOptions, Student } from '../types/student';
import { fileStamp } from './format';

const COLUMNS = [
  { header: '#', width: 6, key: 'index' },
  { header: 'Student Full Names', width: 28, key: 'fullNames' },
  { header: 'Gender', width: 12, key: 'gender' },
  { header: 'Class', width: 14, key: 'className' },
  { header: "Father's Name", width: 24, key: 'fatherName' },
  { header: "Father's Telephone", width: 20, key: 'fatherPhone' },
  { header: "Mother's Name", width: 24, key: 'motherName' },
  { header: "Mother's Telephone", width: 20, key: 'motherPhone' },
  { header: 'Small Note', width: 44, key: 'note' }
] as const;

const THIN = { style: 'thin', color: { rgb: 'FFD8DCE7' } } as const;
const CELL_BORDER = { top: THIN, bottom: THIN, left: THIN, right: THIN };

export function selectStudentsForExport(students: Student[], options: ExportOptions): Student[] {
  if (options.scope === 'class' && options.className) {
    return students.filter((student) => student.className.trim().toLowerCase() === options.className.toLowerCase());
  }
  if (options.scope === 'gender' && options.gender) {
    return students.filter((student) => student.gender === options.gender);
  }
  return students;
}

export function describeExportScope(options: ExportOptions): string {
  if (options.scope === 'class' && options.className) return `Class: ${options.className}`;
  if (options.scope === 'gender' && options.gender) return `Gender: ${options.gender}`;
  return 'All students';
}

function value(raw: string): string {
  const trimmed = (raw ?? '').trim();
  return trimmed.length > 0 ? trimmed : '—';
}

/** Builds and downloads a formatted .xlsx report from the live Supabase rows. */
export function exportStudentsToExcel(students: Student[], options: ExportOptions): string {
  const scopeLabel = describeExportScope(options);
  const lastColumn = String.fromCharCode(65 + COLUMNS.length - 1);
  const headerRowIndex = 4;

  const sheet: Record<string, unknown> = {};
  const rowCount = headerRowIndex + students.length;
  sheet['!ref'] = `A1:${lastColumn}${Math.max(rowCount, headerRowIndex)}`;

  const setCell = (address: string, cell: Record<string, unknown>) => {
    sheet[address] = cell;
  };

  // Title block
  setCell('A1', {
    t: 's',
    v: 'STUDENT RECORDER — STUDENT REPORT',
    s: {
      font: { bold: true, sz: 16, color: { rgb: 'FFFFFFFF' }, name: 'Calibri' },
      fill: { patternType: 'solid', fgColor: { rgb: 'FF4F46E5' } },
      alignment: { horizontal: 'left', vertical: 'center', indent: 1 }
    }
  });
  setCell('A2', {
    t: 's',
    v: `${scopeLabel}  •  ${students.length} record${students.length === 1 ? '' : 's'}  •  Generated ${new Date().toLocaleString()}  •  Paulin Dev Studio`,
    s: {
      font: { sz: 10, color: { rgb: 'FF475569' }, italic: true, name: 'Calibri' },
      alignment: { horizontal: 'left', vertical: 'center', indent: 1 }
    }
  });

  for (let column = 1; column < COLUMNS.length; column += 1) {
    const letter = String.fromCharCode(65 + column);
    setCell(`${letter}1`, {
      t: 's',
      v: '',
      s: { fill: { patternType: 'solid', fgColor: { rgb: 'FF4F46E5' } } }
    });
    setCell(`${letter}2`, { t: 's', v: '' });
  }

  // Header row
  COLUMNS.forEach((column, index) => {
    const letter = String.fromCharCode(65 + index);
    setCell(`${letter}${headerRowIndex}`, {
      t: 's',
      v: column.header,
      s: {
        font: { bold: true, sz: 11, color: { rgb: 'FFFFFFFF' }, name: 'Calibri' },
        fill: { patternType: 'solid', fgColor: { rgb: 'FF312E81' } },
        alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
        border: CELL_BORDER
      }
    });
  });

  // Data rows
  students.forEach((student, rowIndex) => {
    const excelRow = headerRowIndex + rowIndex + 1;
    const zebra = rowIndex % 2 === 1;
    const cells: (string | number)[] = [
      rowIndex + 1,
      value(student.fullNames),
      value(student.gender),
      value(student.className),
      value(student.fatherName),
      value(student.fatherPhone),
      value(student.motherName),
      value(student.motherPhone),
      value(student.note)
    ];

    cells.forEach((cellValue, columnIndex) => {
      const letter = String.fromCharCode(65 + columnIndex);
      const isNumber = typeof cellValue === 'number';
      setCell(`${letter}${excelRow}`, {
        t: isNumber ? 'n' : 's',
        v: cellValue,
        s: {
          font: { sz: 10, color: { rgb: 'FF0F172A' }, name: 'Calibri' },
          fill: zebra ? { patternType: 'solid', fgColor: { rgb: 'FFF4F5FB' } } : undefined,
          alignment: {
            horizontal: isNumber || columnIndex === 2 ? 'center' : 'left',
            vertical: 'top',
            wrapText: columnIndex === COLUMNS.length - 1
          },
          border: CELL_BORDER
        }
      });
    });
  });

  sheet['!cols'] = COLUMNS.map((column, index) => {
    const longest = students.reduce((max, student) => {
      const raw =
        index === 0
          ? String(students.length)
          : String([
              '',
              student.fullNames,
              student.gender,
              student.className,
              student.fatherName,
              student.fatherPhone,
              student.motherName,
              student.motherPhone,
              index === COLUMNS.length - 1 ? '' : student.note
            ][index] ?? '');
      return Math.max(max, raw.length + 4);
    }, column.header.length + 4);
    return { wch: Math.min(Math.max(column.width, longest), 52) };
  });

  sheet['!rows'] = [{ hpt: 30 }, { hpt: 18 }, { hpt: 8 }, { hpt: 26 }];
  sheet['!merges'] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: COLUMNS.length - 1 } },
    { s: { r: 1, c: 0 }, e: { r: 1, c: COLUMNS.length - 1 } }
  ];
  if (students.length > 0) {
    sheet['!autofilter'] = { ref: `A${headerRowIndex}:${lastColumn}${headerRowIndex + students.length}` };
  }

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, sheet as never, 'Students');

  const fileName = `Student-Recorder-Report-${fileStamp()}.xlsx`;
  XLSX.writeFile(workbook, fileName);
  return fileName;
}
